import { faCaretDown, faCaretUp } from '@fortawesome/pro-regular-svg-icons';
import classnames from 'classnames';
import { ReactNode, useState } from 'react';
import { MessageDescriptor } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { FontAwesomeIcon, IconDefinition } from 'atoms/icons';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyM } from 'atoms/typography';
import { useIntlContext } from 'contexts/intl';
import { useScreenSize } from 'hooks/device/useScreenSize';
import { tabletAndAbove } from 'style/mediaQuery';

export interface LinkProps<ID extends string = string> {
  id: ID;
  label: MessageDescriptor | string;
  icon?: IconDefinition;
  pictureUrl?: string;
  subtitle?: ReactNode;
  to?: string;
  onClick?: () => void;
  activeLink?: ID;
  nested?: LinkProps<ID>[];
  parent?: string;
  toggleMenuOpened?: () => void;
  hidden?: boolean;
}

interface Props<ID extends string = string> extends LinkProps<ID> {
  active?: boolean;
  onClick?: () => void;
  onClickNested?: (id: ID) => void;
}

const StyledButtonBase = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  margin: 0;
  padding: var(--unit) var(--double-unit);
  gap: var(--unit);
  border-bottom: 1px solid var(--c-nd-100);
  &.parentAvailable {
    background-color: rgba(var(--c-rgb-blue-400), 0.25);
  }
  &.active,
  &:hover,
  &:focus {
    background-color: var(--c-nd-100);
    color: var(--c-white);
  }

  ${tabletAndAbove} {
    padding: var(--unit) var(--triple-unit);
  }
  container-type: inline-size;
`;
const LabelContainer = styled(Horizontal).attrs({ gap: 0 })`
  justify-content: flex-start;
  color: inherit;
  width: 100%;

  @container (max-width: 220px) {
    flex-direction: column;

    ${BodyM} {
      font: var(--t-12);
      text-align: center;
    }
  }
`;
const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  margin-right: var(--unit);
  border-radius: 12px;
  ${StyledButtonBase}.active & {
    color: var(--c-white);
  }
`;
const Picture = styled.img`
  width: 32px;
  height: 32px;
  background-size: cover;
  margin-right: var(--unit);
  border-radius: 12px;
`;

export const SideBarLink = <ID extends string = string>(props: Props<ID>) => {
  const { up: isGreaterThanTablet } = useScreenSize('tablet');
  const {
    id: currentId,
    label,
    onClick,
    onClickNested,
    toggleMenuOpened,
    icon,
    pictureUrl,
    active = false,
    subtitle,
    to,
    activeLink,
    nested = [],
    parent,
    hidden = false,
  } = props;

  const { formatMessage } = useIntlContext();
  const nestedIds: ID[] = nested.map(({ id }) => id);
  const isActiveLinkInNested = activeLink
    ? nestedIds.includes(activeLink)
    : false;

  const [expand, setExpand] = useState<boolean>(() => {
    return isActiveLinkInNested;
  });

  const toggleNested = () => {
    setExpand(prevState => !prevState);

    if (isActiveLinkInNested) {
      return;
    }

    if (!isActiveLinkInNested && nestedIds.length && onClickNested) {
      onClickNested(nestedIds[0]);
    }
  };

  if (hidden) {
    return null;
  }

  if (!isActiveLinkInNested && expand) {
    setExpand(false);
  }

  return (
    <Vertical>
      <StyledButtonBase
        to={to}
        as={to ? Link : ButtonBase}
        onClick={nested.length ? toggleNested : onClick}
        {...(to ? {} : { disableDebounce: !!nested.length })}
        className={classnames({
          active: active || activeLink === currentId || isActiveLinkInNested,
          parentAvailable: parent && activeLink && activeLink !== currentId,
        })}
      >
        <LabelContainer>
          {icon && (
            <Icon>
              <FontAwesomeIcon icon={icon} />
            </Icon>
          )}
          {pictureUrl && <Picture alt={pictureUrl} src={pictureUrl} />}
          <div>
            <BodyM>
              {typeof label === 'string' ? label : formatMessage(label)}
            </BodyM>
            {subtitle}
          </div>
        </LabelContainer>
        {nested.length ? (
          <FontAwesomeIcon icon={expand ? faCaretUp : faCaretDown} />
        ) : null}
      </StyledButtonBase>
      {expand &&
        nested.map(element => (
          <SideBarLink
            key={element.id}
            active={activeLink === element.id}
            onClick={() => {
              if (onClickNested) {
                onClickNested(element.id);
                if (!isGreaterThanTablet && toggleMenuOpened) {
                  toggleMenuOpened();
                }
              }
            }}
            activeLink={activeLink}
            {...element}
          />
        ))}
    </Vertical>
  );
};
